// apps/spa/src/components/auth/RouteGuard.tsx
// Wrapper used in <Routes> for protected routes. Reads useAuth() and:
//   - while loading: renders a full-page skeleton placeholder
//   - on session_expired error: navigates to /login
//   - on role_required error: navigates to /auth/role-required
//   - otherwise: renders <AppShell /> which provides the <Outlet />
//
// The ROLE_REQUIRED side-effect inside lib/api.ts also navigates the window
// to /auth/role-required directly; this guard handles the case where the SPA
// has already mounted and an in-app fetch returns role_required mid-session.

import { Navigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { AppShell } from '@/components/layout/AppShell';
import { Skeleton } from '@/components/ui/skeleton';
import { ERROR_CODES } from '@guardian/shared';

export function RouteGuard() {
  const { isLoading, isError, error } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="mt-6 h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    if (error?.code === ERROR_CODES.SESSION_EXPIRED || error?.status === 401) {
      return <Navigate to="/login" replace />;
    }
    if (error?.code === ERROR_CODES.ROLE_REQUIRED) {
      return <Navigate to="/auth/role-required" replace />;
    }
    // Other auth errors fall through to the login redirect — fail-closed.
    return <Navigate to="/login" replace />;
  }

  return <AppShell />;
}
